import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiPost } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, schedulerKeys } from '@/lib/queryKeys';
import type { CreateBookingDto, CreateBookingResponse } from '@/types/scheduler';

/**
 * Creates a new service call via the scheduler microservice.
 */
export function useCreateBooking(options?: { jobPermissions?: boolean }) {
  const queryClient = useQueryClient();

  return useApiMutation<CreateBookingResponse, CreateBookingDto>({
    mutationFn: (dto) =>
      apiPost<CreateBookingResponse, CreateBookingDto>(
        operationsClient,
        dto.jobId &&
          dto.granularity !== 'ALL_DAY' &&
          schedulerEndpoints.bookings() === '/v2/bookings'
          ? `${schedulerEndpoints.bookings()}/jobs/${encodeURIComponent(dto.jobId)}`
          : schedulerEndpoints.bookings(),
        dto,
      ),
    // Every consumer submits through the VisitRequestForm, whose catch maps
    // backend codes (409 day conflicts, NO_SHIFT_ON_DATE, …) to human copy and
    // toasts it — the default raw-message toast here would double up with that.
    showErrorToast: false,
    onSuccess: (_response, dto) => {
      // NOT-555 — the Requests table reads `bookingKeys.*` (useBookings) while the
      // dispatch/scheduler views read `schedulerKeys.bookings()`. Invalidate both,
      // matching useUpdateBooking, so the new request appears without a manual
      // reload.
      void queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.bookings() });
      void queryClient.invalidateQueries({ queryKey: ['scheduling-plan-board'] });
      toast.success(
        dto.jobId || options?.jobPermissions ? 'Visit created' : 'Service call created',
      );
    },
  });
}
